import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { seatRequestForCandidate } from '../../../lib/seatingCandidates';
import type { SeatingProgress as Progress } from '../../../lib/seatingCandidates';
import type { AssignSeatRequest } from '../../../types/seating';

/** Enough to see who is left without burying the room below a 137-name list. */
const VISIBLE_UNSEATED = 12;

/**
 * How far along the room is, and who is still standing.
 *
 * Two numbers, kept apart on purpose: guests seated is the question the host
 * asked, chairs put out is the one that catches a chart that cannot finish.
 * A chart with every guest seated can still be short of chairs for their
 * plus-ones, and that only shows up at the venue.
 *
 * Every unseated name is a "Seat" button that arms the next chair tap. That is
 * the touch path — pick the person, then tap where they go — and it is faster
 * than opening a chair and searching for them by name.
 */
export default function SeatingProgress({
  progress,
  placing,
  onPlace,
  onCancelPlacing,
}: {
  progress: Progress;
  /** Who the next chair tap seats, when a placement or move is armed. */
  placing?: string | null;
  onPlace: (request: AssignSeatRequest, occupantName: string) => void;
  onCancelPlacing: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');
  const [showOthers, setShowOthers] = useState(false);

  const needle = query.trim().toLowerCase();
  const narrow = (list: Progress['unseated']) =>
    needle ? list.filter((c) => c.displayName.toLowerCase().includes(needle)) : list;
  const matching = useMemo(() => narrow(progress.unseated), [progress.unseated, needle]);
  const others = useMemo(() => narrow(progress.unseatedOther), [progress.unseatedOther, needle]);
  const percent = progress.totalGuests > 0 ? (progress.seatedGuests / progress.totalGuests) * 100 : 0;
  const shortOfChairs = progress.chairsTotal < progress.seatsNeeded;
  const unseated = showAll ? matching : matching.slice(0, VISIBLE_UNSEATED);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          {progress.seatedGuests} of {progress.totalGuests} seated
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {progress.chairsUsed} of {progress.chairsTotal} chairs filled
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(percent, 100)}
        sx={{ mt: 1, mb: 1.5, height: 8, borderRadius: 4 }}
      />

      {placing ? (
        <Alert
          severity="info"
          sx={{ mb: 1.5 }}
          action={<Button size="small" onClick={onCancelPlacing}>Cancel</Button>}
        >
          Tap a chair to seat {placing}.
        </Alert>
      ) : null}

      {shortOfChairs ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {progress.seatsNeeded} chairs are needed — plus-ones included — and the room has{' '}
          {progress.chairsTotal}. Add a table.
        </Alert>
      ) : null}

      {/*
        A search box, because "who am I missing" is answered by reading this
        list and the list is the length of the party. Filtering it is how a host
        checks one specific person they think they forgot, without scrolling.
      */}
      {progress.unseated.length + progress.unseatedOther.length > 6 ? (
        <TextField
          size="small"
          fullWidth
          placeholder="Filter who's left"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ mb: 1.5 }}
        />
      ) : null}

      {progress.unseated.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {progress.totalGuests > 0
            ? 'Everyone attending has a chair.'
            : 'Nobody has claimed a spot at this dinner yet.'}
        </Typography>
      ) : matching.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nobody still to seat matches “{query}”.
        </Typography>
      ) : (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {`Still to seat — ${progress.unseated.length} of the ${progress.totalGuests} coming`}
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {unseated.map((candidate) => (
              <Box
                key={candidate.inviteeId || candidate.displayName}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
              >
                <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="body2" noWrap>{candidate.displayName}</Typography>
                  {candidate.seatsNeeded > 1 ? (
                    <Chip size="small" variant="outlined" label={`party of ${candidate.seatsNeeded}`} />
                  ) : null}
                </Box>
                <Button
                  size="small"
                  onClick={() => onPlace(seatRequestForCandidate(candidate), candidate.displayName)}
                >
                  Seat
                </Button>
              </Box>
            ))}
          </Stack>
          {matching.length > VISIBLE_UNSEATED ? (
            <Button size="small" sx={{ mt: 0.5 }} onClick={() => setShowAll((prev) => !prev)}>
              {showAll ? 'Show fewer' : `Show all ${matching.length}`}
            </Button>
          ) : null}
        </Box>
      )}

      {/*
        Everyone else without a chair, kept and counted rather than dropped. They
        are not the answer to "who am I missing" — they never said they were
        coming — but a guest who never replied and turns up is a real problem,
        and the host is the one who decides whether to seat them.
      */}
      {others.length > 0 ? (
        <Box sx={{ mt: 1.5 }}>
          <Button size="small" onClick={() => setShowOthers((prev) => !prev)}>
            {showOthers
              ? 'Hide the rest of the list'
              : `${others.length} more on the list who haven't said yes`}
          </Button>
          {showOthers ? (
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {others.slice(0, VISIBLE_UNSEATED).map((candidate) => (
                <Box
                  key={candidate.inviteeId || candidate.displayName}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                >
                  <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="body2" noWrap color="text.secondary">
                      {candidate.displayName}
                    </Typography>
                    <Chip size="small" variant="outlined" label={candidate.rsvpStatus || 'no reply'} />
                  </Box>
                  <Button
                    size="small"
                    onClick={() => onPlace(seatRequestForCandidate(candidate), candidate.displayName)}
                  >
                    Seat
                  </Button>
                </Box>
              ))}
            </Stack>
          ) : null}
        </Box>
      ) : null}
    </Paper>
  );
}
